/**
 * ViewFuecDialog Component
 * Modal para visualizar detalle de un FUEC
 */

import React, { useCallback, useState } from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Button,
  Divider,
  Row,
  Col,
  Card,
  Typography,
  Timeline,
  message,
} from "antd";
import {
  DownloadOutlined,
  PrinterOutlined,
  FileTextOutlined,
  CarOutlined,
  UserOutlined,
  BankOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  QrcodeOutlined,
  HistoryOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import api from "../../../axiosConfig";

const { Text, Title } = Typography;

const ViewFuecDialog = ({ visible, fuec, onClose, onDownload, canExport = true, canPrint, canDownload }) => {
  // Ver/imprimir y descargar pueden tener permisos distintos (menús de Centro de Control y Coordinación)
  const puedeImprimir = canPrint ?? canExport;
  const puedeDescargar = canDownload ?? canExport;
  const [printing, setPrinting] = useState(false);

  const handleDownload = useCallback(() => {
    if (fuec && onDownload) {
      onDownload(fuec);
    }
  }, [fuec, onDownload]);

  const handlePrint = useCallback(async () => {
    if (!fuec) return;

    setPrinting(true);
    try {
      // Obtener PDF con autenticación
      const response = await api.get(`/fuec/${fuec.id}/visualizar-pdf`, {
        responseType: 'blob',
      });

      // Crear URL del blob y abrir en nueva ventana
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');

      // Limpiar URL después de un tiempo
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        };
      }
    } catch (error) {
      console.error('Error al abrir PDF:', error);
      message.error('Error al cargar el PDF para imprimir');
    } finally {
      setPrinting(false);
    }
  }, [fuec]);

  if (!fuec) {
    return null;
  }

  // Configuracion de estado
  const estadoConfig = {
    activo: {
      color: "green",
      icon: <CheckCircleOutlined />,
      text: "Activo",
    },
    vencido: {
      color: "orange",
      icon: <ClockCircleOutlined />,
      text: "Vencido",
    },
    anulado: {
      color: "red",
      icon: <StopOutlined />,
      text: "Anulado",
    },
  };

  const estadoCfg = estadoConfig[fuec.estado] || {
    color: "default",
    text: fuec.estado,
  };

  // Calcular dias de vigencia restantes
  const hoy = dayjs();
  const fechaFin = dayjs(fuec.fecha_fin_vigencia);
  const diasRestantes = fechaFin.diff(hoy, "day");

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Detalle FUEC</span>
          <Tag color={estadoCfg.color} icon={estadoCfg.icon}>
            {estadoCfg.text}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
        puedeImprimir && (
          <Button
            key="print"
            icon={printing ? <LoadingOutlined /> : <PrinterOutlined />}
            onClick={handlePrint}
            loading={printing}
          >
            {printing ? 'Cargando...' : 'Imprimir'}
          </Button>
        ),
        puedeDescargar && (
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
          >
            Descargar PDF
          </Button>
        ),
      ].filter(Boolean)}
    >
      {/* Numero FUEC destacado */}
      <Card
        style={{
          marginBottom: 16,
          background: "linear-gradient(135deg, #1a5276 0%, #2980b9 100%)",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Title level={3} style={{ color: "#fff", margin: 0 }}>
          FUEC No. {fuec.numero_fuec}
        </Title>
        <Text style={{ color: "#d6eaf8" }}>
          Consecutivo: {fuec.consecutivo} | Version: {fuec.version_contrato}
        </Text>
      </Card>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          {/* Datos del Cliente */}
          <Card
            size="small"
            title={
              <Space>
                <BankOutlined /> Cliente / Contratante
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nombre">
                {fuec.cliente_nombre}
              </Descriptions.Item>
              <Descriptions.Item label="NIT/CC">
                {fuec.cliente_nit || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="No. Contrato">
                {fuec.numero_contrato}
              </Descriptions.Item>
              {fuec.convenio_nombre && (
                <>
                  <Descriptions.Item label="Convenio">
                    {fuec.convenio_nombre}
                  </Descriptions.Item>
                  <Descriptions.Item label="NIT Convenio">
                    {fuec.convenio_nit}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>

          {/* Responsable contratante: el editado al generar el FUEC o, si no, los datos del cliente */}
          <Card
            size="small"
            title={
              <Space>
                <UserOutlined /> Responsable Contratante
                {!fuec.responsable_nombre && (
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: "normal" }}>(datos del cliente)</Text>
                )}
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nombre">
                {(fuec.responsable_nombre ? fuec.responsable_nombre : fuec.cliente_nombre) || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Identificación">
                {(fuec.responsable_nombre ? fuec.responsable_identificacion : fuec.cliente_nit) || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Teléfono">
                {(fuec.responsable_nombre ? fuec.responsable_telefono : fuec.cliente_telefono) || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Dirección">
                {(fuec.responsable_nombre ? fuec.responsable_direccion : fuec.cliente_direccion) || "N/A"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Datos del Vehiculo */}
          <Card
            size="small"
            title={
              <Space>
                <CarOutlined /> Vehiculo
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Placa">
                <Tag color="blue">{fuec.vehiculo_placa}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Marca / Modelo">
                {fuec.vehiculo_marca} {fuec.vehiculo_modelo}
              </Descriptions.Item>
              <Descriptions.Item label="Numero Interno">
                {fuec.vehiculo_numero_interno || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Tarjeta Operacion">
                {fuec.vehiculo_tarjeta_operacion || "N/A"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          {/* Datos del Conductor */}
          <Card
            size="small"
            title={
              <Space>
                <UserOutlined /> Conductor
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nombre">
                {fuec.conductor_nombre}
              </Descriptions.Item>
              <Descriptions.Item label="Cedula">
                {fuec.conductor_cedula}
              </Descriptions.Item>
              <Descriptions.Item label="Licencia">
                {fuec.conductor_licencia || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Vigencia Licencia">
                {fuec.conductor_licencia_vigencia
                  ? dayjs(fuec.conductor_licencia_vigencia).format("DD/MM/YYYY")
                  : "N/A"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Vigencia */}
          <Card
            size="small"
            title={
              <Space>
                <CalendarOutlined /> Vigencia
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Fecha Inicio">
                {dayjs(fuec.fecha_inicio_vigencia).format("DD/MM/YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Fin">
                {dayjs(fuec.fecha_fin_vigencia).format("DD/MM/YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Dias Restantes">
                {fuec.estado === "activo" ? (
                  <Tag color={diasRestantes > 30 ? "green" : diasRestantes > 7 ? "orange" : "red"}>
                    {diasRestantes > 0 ? `${diasRestantes} dias` : "Vencido"}
                  </Tag>
                ) : (
                  <Tag color="default">N/A</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Origen y Destino */}
      <Card
        size="small"
        title={
          <Space>
            <EnvironmentOutlined /> Origen y Destino
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Descriptions column={1} size="small" title="Origen">
              <Descriptions.Item label="Ciudad">
                {fuec.origen_ciudad_nombre || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Departamento">
                {fuec.origen_departamento_nombre || "N/A"}
              </Descriptions.Item>
              {fuec.origen_direccion && (
                <Descriptions.Item label="Direccion">
                  {fuec.origen_direccion}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions column={1} size="small" title="Destino">
              <Descriptions.Item label="Ciudad">
                {fuec.destino_ciudad_nombre || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Departamento">
                {fuec.destino_departamento_nombre || "N/A"}
              </Descriptions.Item>
              {fuec.destino_direccion && (
                <Descriptions.Item label="Direccion">
                  {fuec.destino_direccion}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* Objeto del Contrato */}
      <Card
        size="small"
        title={
          <Space>
            <FileTextOutlined /> Objeto del Contrato
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Text style={{ textAlign: "justify", display: "block" }}>
          {fuec.objeto_contrato?.objeto_contrato || fuec.objeto_contrato_texto || "N/A"}
        </Text>
      </Card>

      {/* Traslados Asociados */}
      {fuec.traslados && fuec.traslados.length > 0 && (
        <Card
          size="small"
          title={
            <Space>
              <HistoryOutlined /> Traslados Asociados ({fuec.traslados.length})
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <Timeline
            items={fuec.traslados.map((t) => ({
              color: ["Finalizado", "completado"].includes(t.status || t.estado) ? "green" : "blue",
              children: (
                <div>
                  <strong>#{t.id}</strong> - {t.paciente_nombre || t.status || "Sin paciente"}
                  <br />
                  <Text type="secondary">
                    {t.fecha
                      ? `${dayjs(t.fecha).format("DD/MM/YYYY")} ${(t.hora_recogida || "").substring(0, 5)}`
                      : t.fecha_traslado ? dayjs(t.fecha_traslado).format("DD/MM/YYYY HH:mm") : "Sin fecha"} |{" "}
                    {t.origen_direccion || t.origen || "—"} → {t.destino_direccion || t.destino || "—"}
                  </Text>
                </div>
              ),
            }))}
          />
        </Card>
      )}

      {/* Informacion adicional */}
      <Divider />

      <Descriptions size="small" column={3}>
        <Descriptions.Item label="Creado por">
          {fuec.creado_por?.nombre || fuec.usuario_creador?.nombre || fuec.created_by || "Sistema"}
        </Descriptions.Item>
        <Descriptions.Item label="Fecha Creacion">
          {dayjs(fuec.created_at).format("DD/MM/YYYY HH:mm")}
        </Descriptions.Item>
        <Descriptions.Item label="Descargas">
          {fuec.descargas_count || 0}
        </Descriptions.Item>
        {fuec.estado === "anulado" && (
          <>
            <Descriptions.Item label="Anulado por">
              {fuec.anulado_por?.nombre || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Anulacion">
              {fuec.anulado_at
                ? dayjs(fuec.anulado_at).format("DD/MM/YYYY HH:mm")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Motivo">
              {fuec.motivo_anulacion || "N/A"}
            </Descriptions.Item>
          </>
        )}
      </Descriptions>

      {/* QR Code preview */}
      {fuec.qr_svg && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Divider>
            <QrcodeOutlined /> Codigo QR de Verificacion
          </Divider>
          <div
            dangerouslySetInnerHTML={{ __html: fuec.qr_svg }}
            style={{ maxWidth: 150, margin: "0 auto" }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Escanee para verificar autenticidad
          </Text>
        </div>
      )}
    </Modal>
  );
};

ViewFuecDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  fuec: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  canExport: PropTypes.bool,
  canPrint: PropTypes.bool,
  canDownload: PropTypes.bool,
};

ViewFuecDialog.defaultProps = {
  fuec: null,
  onDownload: null,
  canExport: true,
  canPrint: undefined,
  canDownload: undefined,
};

export default ViewFuecDialog;
